import { useMemo } from 'react';
import { useAppSelector } from './useAppSelector';
import { useUserSelector } from './useUserSelector';
import { IUser } from '~/models/User';

/**
 * Hook to get office details by ID
 *
 * Returns the full office object (IUser) for the given officeId.
 * If no officeId is provided, returns the current user.
 * Searches in current user first, then in child offices.
 *
 * @param officeId - Optional office ID to look up
 * @returns IUser object if found, null otherwise
 *
 * @example
 * ```tsx
 * const office = useGetOfficeById('office_123');
 * const officeName = office?.office_name;
 * ```
 */
export const useGetOfficeById = (officeId?: string): IUser | null => {
  const currentUser = useUserSelector();
  const childOffices = useAppSelector((state) => state.user.childOffices);

  return useMemo(() => {
    if (!currentUser) return null;

    const targetOfficeId = officeId || currentUser.id;

    if (currentUser.id === targetOfficeId) {
      return currentUser;
    }

    return childOffices?.find((it) => it.id === targetOfficeId) || null;
  }, [childOffices, currentUser, officeId]);
};
